import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plug, Bell, ChevronRight, CheckCircle, AlertCircle, Loader2, Save, ShieldCheck, Unplug,
  Briefcase, CreditCard, Mail, MessageSquare, KeyRound, ExternalLink, Eraser, Undo2, RefreshCw, Landmark,
} from 'lucide-react';
import { ticketAlertsAPI, adminProvidersAPI, portalSettingsAPI, adminLookupAPI } from '../../../services/api';
import Card from '../../../components/admin/Card';
import CopyField from '../../../components/admin/CopyField';
import Why from '../../../components/admin/Why';
import WizardSteps from '../../../components/admin/WizardSteps';

/**
 * Admin → Integrations. One card per provider, each self-contained: its own
 * read of the provider list, its own dirty state, its own Save and Verify —
 * the GlobalDisplaySettingsCard shape, rather than one page-wide form whose
 * save would re-post every provider's values at once.
 *
 * Secrets never come back from the server. A stored one renders as an empty
 * password input that says so; leaving it blank keeps it, typing replaces
 * it, and Clear sends '' to remove it. That is the tri-state contract the
 * backend enforces, mirrored here so the form cannot accidentally send a
 * secret it did not mean to.
 */

const CATEGORIES = [
  { id: 'psa', label: 'PSA', icon: Briefcase },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'sso', label: 'Sign-in', icon: KeyRound },
];

const PROVIDERS_KEY = ['admin-providers'];

const healthy = (p) => Boolean(p?.status?.configured && p.status.enabled && !p.status.lastError);

/** Platform payments can take a charge: Stripe has said so (the row follows charges_enabled). */
const platformPaymentsActive = (p) => p?.platformPayments?.state === 'active';

/** Whether a payment provider could be the default gateway right now. */
const gatewayReady = (p) => (p?.key === 'stripe_connect' ? platformPaymentsActive(p) : healthy(p));

function StatusChip({ provider }) {
  if (!provider) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…
      </span>
    );
  }
  if (!provider.supported) {
    return <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500 dark:bg-neutral-700 dark:text-neutral-300">Coming soon</span>;
  }
  if (healthy(provider)) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
        <CheckCircle className="h-3.5 w-3.5" /> {provider.storage === 'chat_installations' ? 'Connected' : 'Verified'}
      </span>
    );
  }
  if (provider.status.configured) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400" title={provider.status.lastError || 'Disabled'}>
        <AlertCircle className="h-3.5 w-3.5" /> Needs attention
      </span>
    );
  }
  return <span className="text-xs text-neutral-500 dark:text-neutral-400">Not configured</span>;
}

const inputClass =
  'w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-60 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white';

/**
 * A setting picked from the MSP's own ConnectWise — a board by name, a status
 * from that board, a member, a custom field by caption — instead of an id
 * nobody can be expected to know. The stored value is unchanged (the id, or
 * the caption); only the asking changed. `current(name)` reads another
 * field's live value, which is how the status lists follow the board.
 */
const LOOKUP_CALLS = {
  boards: () => adminLookupAPI.boards(),
  statuses: (boardId) => adminLookupAPI.boardStatuses(boardId),
  members: () => adminLookupAPI.members(),
  customFields: (_boardId, screen) => adminLookupAPI.customFields(screen),
};

function lookupOption(kind, item) {
  if (kind === 'customFields') return { value: item.caption, label: item.type ? `${item.caption} (${item.type})` : item.caption };
  if (kind === 'members') return { value: String(item.id), label: item.identifier ? `${item.name} (${item.identifier})` : item.name };
  return { value: String(item.id), label: item.name };
}

function LookupSelect({ id, field, value, onChange, disabled, current }) {
  const { kind, boardField, screen } = field.lookup;
  const boardId = kind === 'statuses' ? current(boardField) : null;
  const needsBoard = kind === 'statuses' && !boardId;
  const { data: items, isLoading, isError } = useQuery({
    queryKey: ['cw-lookup', kind, screen || boardId || null],
    queryFn: () => LOOKUP_CALLS[kind](boardId, screen),
    enabled: !needsBoard,
    retry: false,
    select: (resp) => (resp.data?.items || []).filter((i) => !i.inactiveFlag),
  });
  const v = value === null || value === undefined ? '' : String(value);
  const options = (items || []).map((i) => lookupOption(kind, i));

  // Nothing to choose from — ConnectWise could not be read, or the screen
  // has no custom fields yet — so the plain input is the honest control.
  if (isError || (!isLoading && !needsBoard && options.length === 0)) {
    return (
      <>
        <input id={id} type={field.type === 'number' ? 'number' : 'text'} value={v} onChange={(e) => onChange(e.target.value)} disabled={disabled} autoComplete="off" className={inputClass} />
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
          {isError ? 'Could not read the list from ConnectWise; enter the value by hand.' : 'ConnectWise has nothing to offer here yet; enter the value by hand.'}
        </p>
      </>
    );
  }

  const known = options.some((o) => o.value === v);
  return (
    <select id={id} value={v} onChange={(e) => onChange(e.target.value)} disabled={disabled || needsBoard || isLoading} className={inputClass}>
      <option value="">{needsBoard ? 'Choose the board first' : isLoading ? 'Loading from ConnectWise…' : '— portal default —'}</option>
      {v && !known && !isLoading && <option value={v}>{v} (not found in ConnectWise)</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Field({ field, provider, value, touched, onChange, disabled, lookupReady, current }) {
  const stored = provider.storedSecrets?.includes(field.name);
  const clearing = stored && touched && value === '';
  const id = `${provider.key}-${field.name}`;

  let control;
  if (field.lookup && lookupReady && !field.secret) {
    control = <LookupSelect id={id} field={field} value={value} onChange={onChange} disabled={disabled} current={current} />;
  } else if (field.secret) {
    control = (
      <div className="flex items-center gap-2">
        {field.type === 'textarea' ? (
          <textarea
            id={id}
            rows={4}
            autoComplete="new-password"
            spellCheck={false}
            value={clearing ? '' : value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled || clearing}
            placeholder={stored ? 'One is stored; leave blank to keep it' : ''}
            className={`${inputClass} font-mono text-xs`}
          />
        ) : (
          <input
            id={id}
            type="password"
            autoComplete="new-password"
            spellCheck={false}
            value={clearing ? '' : value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled || clearing}
            placeholder={stored ? '•••••••• (one is stored; leave blank to keep it)' : ''}
            className={inputClass}
          />
        )}
        {stored && !disabled && (clearing ? (
          <button type="button" onClick={() => onChange(undefined)} className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200" title="Keep the stored value">
            <Undo2 className="h-3.5 w-3.5" /> Keep
          </button>
        ) : (
          <button type="button" onClick={() => onChange('')} className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-red-600" title="Remove the stored value on save">
            <Eraser className="h-3.5 w-3.5" /> Clear
          </button>
        ))}
      </div>
    );
  } else if (field.type === 'select') {
    control = (
      <select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} className={inputClass}>
        <option value="">— choose —</option>
        {(field.options || []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  } else {
    control = (
      <input
        id={id}
        type={field.type === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoComplete="off"
        className={inputClass}
      />
    );
  }

  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {field.label}
        {field.required && <span className="ml-1 text-red-500">*</span>}
        {clearing && <span className="ml-2 text-xs font-normal text-red-600">will be cleared on save</span>}
      </label>
      {control}
      {field.help && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{field.help}</p>}
    </div>
  );
}

/**
 * One provider. Owns its slice of the provider list (React Query dedupes
 * the request across cards), its touched values, and its mutations.
 */
/**
 * SMTP2GO mode: the CNAMEs the MSP must publish before mail from their
 * domain authenticates, each with SMTP2GO's view of whether it resolves.
 * The sender is refused until DKIM and the return path verify — a
 * half-verified sender bounces every customer email — so this panel is the
 * thing standing between "key saved" and "mail goes out".
 */
function SenderDomainPanel({ provider }) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState(null);
  const dns = provider.dns;
  const checkMutation = useMutation({
    mutationFn: () => adminProvidersAPI.verifySenderDomain(),
    onSuccess: (res) => {
      setOutcome({ ok: res.data.ok, text: res.data.ok ? `${res.data.dns?.domain} is verified.` : `${res.data.dns?.domain} is not verified yet. Publish the records below and check again; DNS can take a few minutes to propagate.` });
      queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
    },
    onError: (error) => setOutcome({ ok: false, text: error.response?.data?.error || 'Could not check the sender domain' }),
  });

  if (!provider.status.configured || !dns) return null;
  const records = dns.records || [];
  const onPlatformDomain = dns.included && dns.platformDomain && dns.domain === dns.platformDomain;

  return (
    <div className="mt-5 rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
      {dns.included && (
        <p className="mb-3 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
          {onPlatformDomain
            ? <>Email delivery is included with your portal: mail goes out from <code>{dns.domain}</code>, already verified, and nothing needs publishing. To send from your own domain instead, change the From address above to one on your domain, save, and publish the records that then appear here.</>
            : <>Email delivery is included with your portal. You have moved the From address to your own domain, so its records below must verify before mail goes out from it.</>}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Sender domain {dns.domain ? <code>{dns.domain}</code> : null}</h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {dns.verified
              ? 'Verified. Mail from this domain is signed and will authenticate.'
              : 'Not verified. The portal will not send from this domain until the records below resolve.'}
            {dns.checkedAt && <> Last checked {new Date(dns.checkedAt).toLocaleString()}.</>}
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${dns.verified ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}`}>
          {dns.verified ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
          {dns.verified ? 'Verified' : 'Pending'}
        </span>
      </div>

      {records.length > 0 ? (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-neutral-500 dark:text-neutral-400">
                <th className="py-1 pr-3 font-medium">Purpose</th>
                <th className="py-1 pr-3 font-medium">Type</th>
                <th className="py-1 pr-3 font-medium">Host</th>
                <th className="py-1 pr-3 font-medium">Value</th>
                <th className="py-1 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.host} className="border-t border-neutral-100 dark:border-neutral-700">
                  <td className="py-1.5 pr-3 capitalize text-neutral-700 dark:text-neutral-300">{r.purpose}{r.optional ? ' (optional)' : ''}</td>
                  <td className="py-1.5 pr-3 text-neutral-700 dark:text-neutral-300">{r.type}</td>
                  <td className="py-1.5 pr-3"><code className="select-all">{r.host}</code></td>
                  <td className="py-1.5 pr-3"><code className="select-all">{r.value}</code></td>
                  <td className="py-1.5">
                    {r.verified
                      ? <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400"><CheckCircle className="h-3.5 w-3.5" /> OK</span>
                      : <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><AlertCircle className="h-3.5 w-3.5" /> Missing</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
          No records yet. Check the domain to register it with SMTP2GO and fetch the records to publish.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => checkMutation.mutate()}
          disabled={checkMutation.isPending}
          className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
        >
          {checkMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
          {records.length ? 'Check DNS now' : 'Register domain and fetch records'}
        </button>
        {outcome && (
          <span className={`inline-flex items-center gap-1 text-xs ${outcome.ok ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
            {outcome.ok ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />} {outcome.text}
          </span>
        )}
      </div>
    </div>
  );
}

function ProviderCard({ providerKey, icon }) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState({});
  const [outcome, setOutcome] = useState(null); // { ok, text }

  const { data: provider } = useQuery({
    queryKey: PROVIDERS_KEY,
    queryFn: () => adminProvidersAPI.list(),
    select: (res) => res.data.providers.find((p) => p.key === providerKey),
    retry: false,
  });

  const dirty = Object.keys(values).length > 0;
  const reset = () => setValues({});
  // The default-gateway card reads its own endpoint, so a gateway saved or
  // disabled here must refresh it too, or Stripe stays greyed out in it until
  // the page is reloaded.
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
    queryClient.invalidateQueries({ queryKey: ['admin-payment-gateway'] });
  };

  const saveMutation = useMutation({
    mutationFn: () => adminProvidersAPI.save(providerKey, values),
    onSuccess: (res) => {
      reset();
      setOutcome({ ok: true, text: `Saved and verified — ${res.data.detail}` });
      refresh();
    },
    onError: (error) => setOutcome({ ok: false, text: error.response?.data?.error || 'Save failed' }),
  });

  const verifyMutation = useMutation({
    mutationFn: () => adminProvidersAPI.verify(providerKey, values),
    onSuccess: (res) => {
      setOutcome({ ok: true, text: res.data.detail });
      if (!dirty) refresh();
    },
    onError: (error) => {
      setOutcome({ ok: false, text: error.response?.data?.detail || error.response?.data?.error || 'Verification failed' });
      if (!dirty) refresh();
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => adminProvidersAPI.disable(providerKey),
    onSuccess: () => {
      reset();
      setOutcome({ ok: true, text: provider?.storage === 'chat_installations' ? 'Disconnected' : 'Disabled. The stored values are kept so it can be re-enabled after a fix.' });
      refresh();
    },
    onError: (error) => setOutcome({ ok: false, text: error.response?.data?.error || 'Could not disable' }),
  });

  if (!provider) {
    return (
      <Card id={`provider-${providerKey}`} icon={icon} title={providerKey} aside={<StatusChip provider={null} />}>
        <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      </Card>
    );
  }

  const busy = saveMutation.isPending || verifyMutation.isPending || disableMutation.isPending;
  const disabled = !provider.supported || busy;
  const oauth = provider.connect?.kind === 'oauth';
  const setField = (name, v) =>
    setValues((prev) => {
      const next = { ...prev };
      const field = provider.fields.find((f) => f.name === name);
      const stored = provider.storedSecrets?.includes(name);
      // Back to untouched: the field drops out of the body entirely, which
      // is what "keep what is stored" means on the wire. An emptied secret
      // input that had nothing stored is untouched too — '' only means
      // "clear" when there is something to clear, and the Clear button is
      // the deliberate way to say it.
      const untouched =
        v === undefined ||
        (!field?.secret && v === (provider.config?.[name] ?? '')) ||
        (field?.secret && v === '' && !stored);
      if (untouched) delete next[name];
      else next[name] = v;
      return next;
    });

  // Lists can be read from the provider only once its connection is stored
  // and verified; before that the same fields take typed values.
  const lookupReady = Boolean(provider.status.configured && provider.status.enabled);
  const current = (name) => (name in values ? values[name] : provider.config?.[name] ?? '');
  const groups = provider.fields.some((f) => f.group)
    ? [
        { id: 'connection', title: 'Connection', intro: null },
        {
          id: 'settings',
          title: 'Portal defaults',
          intro: lookupReady
            ? 'Chosen from your ConnectWise. Leave a field on "portal default" to keep the built-in value.'
            : 'Verify and save the connection first; these then become lists read from your ConnectWise. Until then they take ids and captions typed by hand.',
        },
      ]
    : null;
  const renderFields = (fields) => (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((field) => (
        <div key={field.name} className={field.type === 'textarea' ? 'sm:col-span-2' : undefined}>
          <Field
            field={field}
            provider={provider}
            value={field.name in values ? values[field.name] : field.secret ? '' : provider.config?.[field.name] ?? ''}
            touched={field.name in values}
            onChange={(v) => setField(field.name, v)}
            disabled={disabled}
            lookupReady={lookupReady}
            current={current}
          />
        </div>
      ))}
    </div>
  );

  const aside = (
    <div className="flex flex-col items-end gap-2">
      <StatusChip provider={provider} />
      {provider.status.lastVerifiedAt && (
        <span className="text-xs text-neutral-400">Verified {new Date(provider.status.lastVerifiedAt).toLocaleString()}</span>
      )}
    </div>
  );

  return (
    <Card id={`provider-${providerKey}`} icon={icon} title={provider.label} description={provider.description} aside={aside}>
      {provider.status.lastError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <div className="font-medium">Last check failed</div>
            <div>{provider.status.lastError}</div>
          </div>
        </div>
      )}

      {provider.installation && (
        <p className="mb-4 text-sm text-neutral-600 dark:text-neutral-300">
          Connected to <strong>{provider.installation.workspaceName || provider.installation.workspaceId}</strong>
          {provider.installation.installedAt && <> since {new Date(provider.installation.installedAt).toLocaleDateString()}</>}.
          {' '}Boards and channels are routed on the{' '}
          <Link to="/admin/integrations/ticket-alerts" className="text-primary-600 hover:underline dark:text-primary-400">Ticket Alerts</Link> page.
        </p>
      )}

      {provider.callbackUrl && (
        <div className="mb-4">
          <p className="mb-1 text-xs font-medium text-neutral-600 dark:text-neutral-300">Redirect URI</p>
          <CopyField value={provider.callbackUrl} />
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Register this redirect URI with your identity provider.</p>
        </div>
      )}

      {provider.webhookUrl && (
        <div className="mb-4">
          <p className="mb-1 text-xs font-medium text-neutral-600 dark:text-neutral-300">Webhook URL</p>
          <CopyField value={provider.webhookUrl} />
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            {provider.key === 'stripe'
              ? 'Add this as an endpoint in Stripe under Developers → Webhooks, then paste the signing secret (whsec_…) Stripe shows into the field above. Until it is saved every Stripe event is refused. The URL is unique to this portal and does not change.'
              : 'Paste this into Authorize.net under Account → Webhooks (generate a Signature Key there first and enter it in the Signature Key field below). It is unique to this portal and does not change.'}
          </p>
          {provider.webhookHealth && provider.webhookHealth.total > 0 && (
            <p className={`mt-1 text-xs ${provider.webhookHealth.refused > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-neutral-500 dark:text-neutral-400'}`}>
              {provider.webhookHealth.refused > 0
                ? `${provider.webhookHealth.refused} of ${provider.webhookHealth.total} events in the last ${provider.webhookHealth.days} days were refused unread (last reason: ${provider.webhookHealth.lastRefusal}). ${provider.webhookHealth.lastRefusal === 'Invalid signature' ? 'The Signature Key saved here does not match the one in Authorize.net.' : 'Save the Signature Key below and they will be accepted.'}`
                : `${provider.webhookHealth.total} events received in the last ${provider.webhookHealth.days} days, all accepted.`}
            </p>
          )}
        </div>
      )}

      {oauth ? (
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={provider.connect.path}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <ExternalLink className="h-4 w-4" /> {provider.connect.label || 'Connect'}
          </Link>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Slack installs by OAuth: the token is minted by Slack during the redirect and never typed.</p>
        </div>
      ) : groups ? (
        groups.map(({ id: groupId, title, intro }) => {
          const fields = provider.fields.filter((f) => (f.group || 'connection') === groupId);
          if (fields.length === 0) return null;
          return (
            <div key={groupId} className="mb-6 last:mb-0">
              <h4 className="mb-1 text-sm font-semibold text-neutral-800 dark:text-neutral-100">{title}</h4>
              {intro && <p className="mb-3 text-xs text-neutral-500 dark:text-neutral-400">{intro}</p>}
              {renderFields(fields)}
            </div>
          );
        })
      ) : (
        renderFields(provider.fields)
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-700">
        {!oauth && provider.supported && (
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={!dirty || busy}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Verify &amp; save
          </button>
        )}
        {provider.supported && (provider.status.configured || dirty) && (
          <button
            type="button"
            onClick={() => verifyMutation.mutate()}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
          >
            {verifyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {dirty ? 'Test these values' : 'Test connection'}
          </button>
        )}
        {provider.status.configured && provider.status.enabled && (
          <button
            type="button"
            onClick={() => {
              if (window.confirm(`${provider.storage === 'chat_installations' ? 'Disconnect' : 'Disable'} ${provider.label}?`)) disableMutation.mutate();
            }}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-900/20"
          >
            <Unplug className="h-4 w-4" /> {provider.storage === 'chat_installations' ? 'Disconnect' : 'Disable'}
          </button>
        )}
        {dirty && !busy && (
          <button type="button" onClick={() => { reset(); setOutcome(null); }} className="text-sm text-neutral-500 hover:underline">
            Discard changes
          </button>
        )}
        {outcome && (
          <span className={`inline-flex items-center gap-1 text-sm ${outcome.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {outcome.ok ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />} {outcome.text}
          </span>
        )}
      </div>

      {provider.key === 'connectwise' && (
        <Why>
          <p>The keys belong to an API member in ConnectWise (System → Members → API Members). Give it the same role your portal integrator account has today.</p>
          <p>Saving checks the key pair against ConnectWise first and stores nothing if it fails. A saved key is never shown again; to rotate it, paste the new one over the placeholder.</p>
        </Why>
      )}
      {provider.key === 'smtp' && <SenderDomainPanel provider={provider} />}
      {provider.key === 'smtp' && (
        <Why>
          <p>SMTP2GO mode: the from address's domain is registered as a sender domain when you save, and the CNAMEs to publish appear above. Nothing is sent from it until DKIM and the return path verify — a half-verified sender bounces or spam-folders every customer email.</p>
          <p>Your own SMTP server signs its own mail, so there is no DNS step in that mode. The from name, address and reply-to can be overridden in Portal Settings → Email, on the same domain.</p>
        </Why>
      )}
      {provider.key === 'authorizenet' && (
        <Why>
          <p>Choose sandbox until the day you take a real payment, then switch to production and paste the production keys — sandbox keys do not work on the live gateway, and the check here runs against whichever environment is selected.</p>
          <p>The signature key is only needed once you add the webhook endpoint in Authorize.net.</p>
        </Why>
      )}
      {provider.key === 'stripe' && (
        <Why>
          <p>Use the publishable and secret keys from the same mode of the same account: both test (pk_test_ / sk_test_) while you set up, both live on the day you take a real payment. Saving checks the secret key against Stripe and refuses a live/test mismatch.</p>
          <p>Card details never reach the portal on Stripe: your customers type them into Stripe's own secure form, and the portal stores only the resulting reference. Cards saved on another gateway stay there; they are not moved.</p>
        </Why>
      )}
      {provider.category === 'sso' && (
        <Why>
          <p>Saving proves the client id and secret authenticate against the identity provider. Registration of the redirect URI above, and consent, can only be proven by the first real sign-in — once saved, sign out and use the provider's button on the login page.</p>
        </Why>
      )}
    </Card>
  );
}

/**
 * Which gateway new payments go through, shown once more than one is
 * configured. Only new work moves: saved cards, subscriptions and history
 * stay on the gateway they were made on.
 */
function DefaultGatewayCard({ providers }) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState(null);
  const { data } = useQuery({
    queryKey: ['admin-payment-gateway'],
    queryFn: () => adminProvidersAPI.getPaymentGateway().then((r) => r.data),
    retry: false,
  });
  const mutation = useMutation({
    mutationFn: (gateway) => adminProvidersAPI.setPaymentGateway(gateway),
    onSuccess: (res) => {
      setOutcome({ ok: true, text: `New payments now go through ${data?.labels?.[res.data.gateway] || res.data.gateway}.` });
      queryClient.invalidateQueries({ queryKey: ['admin-payment-gateway'] });
      queryClient.invalidateQueries({ queryKey: ['payment-gateway'] });
    },
    onError: (error) => setOutcome({ ok: false, text: error.response?.data?.error || 'Could not change the gateway' }),
  });

  const configured = ['authorizenet', 'stripe', 'stripe_connect'].filter((k) => gatewayReady(providers.find((p) => p.key === k)));
  // Shown whenever any gateway is ready. It used to hide once there was no
  // choice left, so selecting the only ready gateway made the card (and its
  // confirmation) vanish the moment the save succeeded, which read as a failure.
  if (!data || configured.length === 0) return null;

  return (
    <Card id="payment-gateway" icon={CreditCard} title="Default payment gateway" description="Where new saved cards, payments and recurring payments go.">
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={data.gateway}
          onChange={(e) => {
            const label = data.labels?.[e.target.value] || e.target.value;
            if (window.confirm(`Send new payments through ${label}? Cards your customers saved on the other gateway will no longer be offered; they will need to add a card again.`)) mutation.mutate(e.target.value);
          }}
          disabled={mutation.isPending}
          className={`${inputClass} max-w-xs`}
        >
          {(data.selectable || configured).map((k) => (
            <option key={k} value={k} disabled={!(data.available || []).includes(k)}>{data.labels?.[k] || k}</option>
          ))}
        </select>
        {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />}
        {outcome && (
          <span className={`inline-flex items-center gap-1 text-sm ${outcome.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {outcome.ok ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />} {outcome.text}
          </span>
        )}
      </div>
      <Why>
        <p>Existing recurring payments keep charging through the gateway they were created on, and its webhook keeps working. Saved cards are not moved between gateways — no processor allows that from a portal — so customers add a card again on the new one.</p>
      </Why>
    </Card>
  );
}

/** 0.5 for 50 bps, written for a person: "0.5%". */
const percentOfBps = (bps) => `${(Number(bps) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;

/**
 * What the MSP pays, in words that invent no numbers: Stripe's rates are
 * Stripe's to state (and differ by country, card and plan), so with the
 * account paying Stripe the card says whose rates apply and who collects
 * them, and adds only the platform's own fee, which is the one figure this
 * portal actually knows.
 */
function PlatformPaymentsRates({ info }) {
  const fee = Number(info.feeBps) || 0;
  if (info.feePayer === 'application') {
    return (
      <p className="text-sm text-neutral-600 dark:text-neutral-300">
        {fee > 0
          ? <>Processing is billed through your provider: <strong>{percentOfBps(fee)}</strong> of each payment is deducted before it is paid out to you.</>
          : <>Processing is billed through your provider. No per-payment fee is set yet; ask your provider for your rate before taking payments.</>}
      </p>
    );
  }
  return (
    <p className="text-sm text-neutral-600 dark:text-neutral-300">
      Stripe&apos;s standard processing rates apply, and Stripe collects them from each payment.
      {fee > 0 && <> A platform fee of <strong>{percentOfBps(fee)}</strong> of each payment is also deducted.</>}
      {' '}Payouts go from Stripe to your bank.
    </p>
  );
}

/**
 * Platform payments (Stripe Connect). No fields: the MSP connects through
 * Stripe's hosted onboarding, which this card starts and resumes, and Stripe
 * sends them back here (`?platformPayments=return`), where the status is
 * re-read. Shown only when the platform offers it — the server says so in
 * the describe response, the browser never reads the platform's settings.
 */
function PlatformPaymentsCard({ providerKey }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [outcome, setOutcome] = useState(null);
  const returned = useRef(false);

  const { data: provider } = useQuery({
    queryKey: PROVIDERS_KEY,
    queryFn: () => adminProvidersAPI.list(),
    select: (res) => res.data.providers.find((p) => p.key === providerKey),
    retry: false,
  });

  const fail = (error, fallback) => setOutcome({ ok: false, text: error.response?.data?.error || error.response?.data?.message || fallback });

  const onboard = useMutation({
    mutationFn: () => adminProvidersAPI.platformPaymentsOnboard(),
    onSuccess: (res) => {
      // Stripe's hosted onboarding. The link is single-use and short-lived,
      // so it is followed at once rather than shown.
      if (res.data?.url) window.location.assign(res.data.url);
    },
    onError: (error) => fail(error, 'Could not start onboarding'),
  });

  const refresh = useMutation({
    mutationFn: () => adminProvidersAPI.platformPaymentsRefresh(),
    onSuccess: (res) => {
      const st = res.data?.state || {};
      setOutcome(
        st.chargesEnabled
          ? { ok: true, text: `Ready to take payments${st.payoutsEnabled ? ', with payouts to your bank' : '. Payouts start once Stripe has finished checking your bank details'}.` }
          : { ok: false, text: `Stripe still needs ${st.requirementsDue || 'some'} item(s) before you can take payments. Continue onboarding to finish.` }
      );
      queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
      queryClient.invalidateQueries({ queryKey: ['admin-payment-gateway'] });
    },
    onError: (error) => fail(error, 'Could not refresh the status'),
  });

  const dashboard = useMutation({
    mutationFn: () => adminProvidersAPI.platformPaymentsDashboard(),
    onSuccess: (res) => {
      if (res.data?.url) window.open(res.data.url, '_blank', 'noopener,noreferrer');
    },
    onError: (error) => fail(error, 'Could not open the payments dashboard'),
  });

  // Back from Stripe: `return` means the admin finished (or left) the form,
  // so re-read the account; `refresh` means the link expired before they
  // finished, which is Stripe's cue to mint another.
  const back = searchParams.get('platformPayments');
  useEffect(() => {
    if (!back || returned.current) return;
    returned.current = true;
    if (back === 'return') refresh.mutate();
    else if (back === 'refresh') setOutcome({ ok: false, text: 'That onboarding link expired. Continue onboarding to pick up where you left off.' });
    const next = new URLSearchParams(searchParams);
    next.delete('platformPayments');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [back]);

  if (!provider) return null;
  const info = provider.platformPayments || {};
  // Not offered on this platform, and nothing started: nothing to show.
  if (!info.available && info.state === 'not_started') return null;

  const busy = onboard.isPending || refresh.isPending || dashboard.isPending;
  const chip =
    info.state === 'active' ? (
      <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><CheckCircle className="h-3.5 w-3.5" /> Active</span>
    ) : info.state === 'onboarding_incomplete' ? (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"><AlertCircle className="h-3.5 w-3.5" /> Onboarding incomplete</span>
    ) : (
      <span className="text-xs text-neutral-500 dark:text-neutral-400">Not started</span>
    );
  const button = 'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50';
  const primary = `${button} bg-primary-600 text-white hover:bg-primary-700`;
  const secondary = `${button} border border-neutral-300 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700`;

  return (
    <Card
      id={`provider-${providerKey}`}
      icon={Landmark}
      title="Platform payments"
      description="Take card and bank payments without a merchant account of your own. Stripe verifies your business and pays out to your bank."
      aside={chip}
    >
      <div className="space-y-4">
        <PlatformPaymentsRates info={info} />

        {info.state === 'onboarding_incomplete' && (
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Stripe needs {info.requirementsDue ? `${info.requirementsDue} more item(s)` : 'a few more details'} before you can take payments{info.disabledReason ? ` (${info.disabledReason.replace(/[._]/g, ' ')})` : ''}.
          </p>
        )}
        {info.state === 'active' && !info.payoutsEnabled && (
          <p className="text-sm text-amber-700 dark:text-amber-300">You can take payments; payouts to your bank start once Stripe has finished checking your bank details.</p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {info.state === 'not_started' && (
            <button type="button" className={primary} disabled={busy || !info.available} onClick={() => onboard.mutate()}>
              {onboard.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} Connect
            </button>
          )}
          {info.state === 'onboarding_incomplete' && (
            <button type="button" className={primary} disabled={busy} onClick={() => onboard.mutate()}>
              {onboard.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} Continue onboarding
            </button>
          )}
          {(info.state === 'active' || info.detailsSubmitted) && (
            <button type="button" className={info.state === 'active' ? primary : secondary} disabled={busy} onClick={() => dashboard.mutate()}>
              {dashboard.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} Open payments dashboard
            </button>
          )}
          {info.state !== 'not_started' && (
            <button type="button" className={secondary} disabled={busy} onClick={() => refresh.mutate()}>
              {refresh.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh status
            </button>
          )}
        </div>

        {outcome && (
          <div className={`flex items-start gap-2 text-sm ${outcome.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {outcome.ok ? <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" /> : <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />} {outcome.text}
          </div>
        )}

        <Why>
          <p>Connect opens Stripe&apos;s own sign-up, where you confirm your business and add the bank account payouts go to. Nothing is typed into this portal. When Stripe says you can take payments, choose Platform payments as the default gateway above.</p>
          <p>Refunds and disputes are handled in the payments dashboard. A refund returns the platform fee on that payment; so does a dispute you lose.</p>
        </Why>
      </div>
    </Card>
  );
}

/** The Ticket Alerts module card, kept from the original page. */
function TicketAlertsCard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['ticket-alerts-config'],
    queryFn: () => ticketAlertsAPI.get(),
    retry: false,
  });
  const overview = data?.data;
  const featureDisabled = error?.response?.data?.code === 'FEATURE_DISABLED';

  let chip = null;
  if (isLoading) {
    chip = (
      <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…
      </span>
    );
  } else if (featureDisabled) {
    chip = <span className="text-xs text-neutral-500 dark:text-neutral-400">Feature flag off</span>;
  } else if (overview) {
    const slackOk = !!overview.slack?.installation && !overview.slack.installation.lastError;
    const enabled = overview.config?.enabled;
    const cbErrors = (overview.callbacks?.callbacks || []).some((c) => c.status !== 'active' || c.presentInCw === false);
    if (enabled && slackOk && !cbErrors) {
      chip = (
        <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
          <CheckCircle className="h-3.5 w-3.5" /> Active
        </span>
      );
    } else if (enabled) {
      chip = (
        <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
          <AlertCircle className="h-3.5 w-3.5" /> Needs attention
        </span>
      );
    } else {
      chip = <span className="text-xs text-neutral-500 dark:text-neutral-400">Not enabled</span>;
    }
  }

  return (
    <Link
      to="/admin/integrations/ticket-alerts"
      className="group flex flex-col gap-3 rounded-lg border border-neutral-200 bg-white p-5 transition-colors hover:border-primary-300 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-primary-700"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300">
            <Bell className="h-5 w-5" />
          </span>
          <div>
            <h2 className="font-semibold text-neutral-900 dark:text-white">Ticket Alerts</h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">Slack · Google Chat · Microsoft Teams</p>
          </div>
        </div>
        {chip}
      </div>
      <p className="text-sm text-neutral-600 dark:text-neutral-300">
        Real-time ConnectWise ticket alerts on your phone. Change status, take or assign tickets, and reply
        with notes from chat without opening PSA.
      </p>
      <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-primary-600 group-hover:underline dark:text-primary-400">
        {featureDisabled ? 'Enable & configure' : 'Configure'} <ChevronRight className="h-4 w-4" />
      </span>
    </Link>
  );
}

/**
 * One line that says what a configured provider points at, from the
 * non-secret config the server returns: the PSA site, the sandbox/production
 * choice, the from address, the chat workspace. Nothing secret is in `config`.
 */
function tileSummary(provider) {
  if (!provider) return null;
  const c = provider.config || {};
  if (provider.installation) return provider.installation.workspace_name || provider.installation.workspaceName || 'Connected';
  if (provider.key === 'authorizenet' && c.environment) return c.environment === 'sandbox' ? 'Sandbox (test cards only)' : 'Production (live payments)';
  if (provider.key === 'stripe_connect') return provider.platformPayments?.state === 'active' ? 'Payouts to your bank through the platform' : null;
  const preferred = { connectwise: 'siteUrl', smtp: 'fromAddress', entra: 'tenantId', google: 'hostedDomain', okta: 'issuer' }[provider.key];
  if (preferred && c[preferred]) return String(c[preferred]);
  const first = (provider.fields || []).find((f) => !f.secret && c[f.name]);
  return first ? String(c[first.name]) : null;
}

/**
 * A provider as a tile: name, status, what it points at. Opening one shows
 * its full form under the grid; the page used to stack every form at once,
 * so the eye scanned past six ConnectWise fields to find anything.
 */
function ProviderTile({ provider, providerKey, icon: Icon, open, onOpen }) {
  const summary = tileSummary(provider);
  const label = provider?.label || providerKey;
  return (
    <button
      type="button"
      onClick={() => onOpen(open ? null : providerKey)}
      aria-expanded={open}
      className={`group flex flex-col gap-3 rounded-lg border bg-white p-5 text-left transition-colors dark:bg-neutral-800 ${
        open
          ? 'border-primary-400 ring-1 ring-primary-300 dark:border-primary-600 dark:ring-primary-800'
          : 'border-neutral-200 hover:border-primary-300 dark:border-neutral-700 dark:hover:border-primary-700'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-semibold text-neutral-900 dark:text-white">{label}</h3>
            {summary && <p className="text-xs text-neutral-500 dark:text-neutral-400">{summary}</p>}
          </div>
        </div>
        <StatusChip provider={provider} />
      </div>
      {provider?.description && (
        <p className="line-clamp-2 text-sm text-neutral-600 dark:text-neutral-300">{provider.description}</p>
      )}
      <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-primary-600 group-hover:underline dark:text-primary-400">
        {open ? 'Close' : provider && !provider.supported ? 'Details' : healthy(provider) ? 'Manage' : 'Configure'}
        <ChevronRight className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`} />
      </span>
    </button>
  );
}

const DEFAULT_SUPPORT_EMAIL = 'support@example.com';

export default function IntegrationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The provider whose form is open, carried in the URL so a reload, the
  // guided setup and the return from Stripe's onboarding all land on it.
  const open = searchParams.get('provider') || (searchParams.has('platformPayments') ? 'stripe_connect' : null);
  const openProvider = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key) next.set('provider', key);
    else next.delete('provider');
    setSearchParams(next, { replace: true });
    if (key) {
      // The form mounts on the next render; scroll once it exists.
      setTimeout(() => document.getElementById(`provider-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  };
  const { data: list, error } = useQuery({
    queryKey: PROVIDERS_KEY,
    queryFn: () => adminProvidersAPI.list(),
    retry: false,
  });
  const { data: settings } = useQuery({
    queryKey: ['portal-settings'],
    queryFn: () => portalSettingsAPI.get().then((r) => r.data),
    retry: false,
  });

  const providers = list?.data?.providers || [];
  const byKey = useMemo(() => Object.fromEntries(providers.map((p) => [p.key, p])), [providers]);

  // Step state from live data: a step is done when the thing it asks for is
  // actually working, not when somebody clicked past it.
  const steps = useMemo(() => {
    const brandingDone = Boolean(
      settings &&
        // The default name is the tenant's own, so the server says what it
        // is rather than this page guessing one brand's.
        ((settings.companyName && settings.companyName !== (settings.defaultCompanyName ?? '')) ||
          (settings.supportEmail && settings.supportEmail !== DEFAULT_SUPPORT_EMAIL) ||
          settings.companyLogoUrl ||
          settings.timezoneSource === 'settings')
    );
    const done = {
      connectwise: healthy(byKey.connectwise),
      payments: healthy(byKey.authorizenet) || healthy(byKey.stripe) || platformPaymentsActive(byKey.stripe_connect),
      chat: ['slack', 'gchat', 'teams'].some((k) => healthy(byKey[k])),
      branding: brandingDone,
    };
    const order = [
      { id: 'connectwise', label: 'Connect ConnectWise' },
      { id: 'payments', label: 'Payments (optional)' },
      { id: 'chat', label: 'Chat (optional)' },
      { id: 'branding', label: 'Branding' },
    ];
    let currentAssigned = false;
    return order.map((s) => {
      if (done[s.id]) return { ...s, state: 'done' };
      if (!currentAssigned) {
        currentAssigned = true;
        return { ...s, state: 'current' };
      }
      return { ...s, state: 'todo' };
    });
  }, [byKey, settings]);

  const onSelectStep = (id) => {
    const target = { connectwise: 'connectwise', payments: 'authorizenet', chat: 'slack' }[id];
    if (target) openProvider(target);
    else navigate('/admin/portal-settings');
  };

  const forbidden = error?.response?.status === 403;
  const noTenant = error?.response?.status === 409;

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div>
        <div className="mb-2 flex items-center gap-3">
          <Plug className="h-6 w-6 text-neutral-400" />
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Integrations</h1>
        </div>
        <p className="text-neutral-500 dark:text-neutral-400">
          Connect the portal to your PSA, your payment gateway, your mail and the tools your team already lives in.
        </p>
      </div>

      {forbidden ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          {error.response.data?.error || 'You do not have the integrations permission.'}
        </div>
      ) : noTenant ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          {error.response.data?.error}
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
            <div className="mb-3 text-sm font-medium text-neutral-700 dark:text-neutral-300">Guided setup</div>
            <WizardSteps steps={steps} onSelect={onSelectStep} />
            {list?.data?.tenant && (
              <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
                Configuring <strong>{list.data.tenant.name}</strong>. Credentials are stored encrypted for this portal only and are verified against the vendor before they are saved.
              </p>
            )}
          </div>

          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Modules</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <TicketAlertsCard />
            </div>
          </section>

          {CATEGORIES.map((cat) => {
            const keys = providers.filter((p) => p.category === cat.id).map((p) => p.key);
            if (!keys.length && providers.length) return null;
            return (
              <section key={cat.id}>
                <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  <cat.icon className="h-4 w-4" /> {cat.label}
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {keys.map((key) => (
                    <ProviderTile key={key} providerKey={key} provider={byKey[key]} icon={cat.icon} open={open === key} onOpen={openProvider} />
                  ))}
                </div>
                {keys.includes(open) && (
                  <div className="mt-4">
                    {open === 'stripe_connect' ? (
                      <PlatformPaymentsCard providerKey={open} />
                    ) : (
                      <ProviderCard providerKey={open} icon={cat.icon} />
                    )}
                  </div>
                )}
                {cat.id === 'payments' && (
                  <div className="mt-4">
                    <DefaultGatewayCard providers={providers} />
                  </div>
                )}
              </section>
            );
          })}

          {!providers.length && !error && (
            <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading providers…</div>
          )}
        </>
      )}
    </div>
  );
}
